import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';
import { CardComponent } from 'src/app/shared/components/master-detail-layout/card/card.component';
import { MasterDetailLayoutComponent } from 'src/app/shared/components/master-detail-layout/master-detail-layout.component';
import { SidebarItemComponent } from 'src/app/shared/components/master-detail-layout/sidebar-item/sidebar-item.component';
import { SidebarSectionComponent } from 'src/app/shared/components/master-detail-layout/sidebar-section/sidebar-section.component';
import { FidelityDataCatalogService } from '../../services/fidelity-data-catalog.service';
import { ChainCampaigns, ChainRules, Items } from 'tilby-models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FidelityCampaignFormService } from '../../services/fidelity-campaign-form.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CampaignPrizesFields, CampaignsFields, CampaignsFormGroups } from '../../models/fidelity-campaign.models';
import { ListSelectorDialogService } from 'src/app/dialogs/list-selector-dialog/list-selector-dialog.component';
import { TruncatedTextDirective } from 'src/app/directives/truncated-text.directive';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FidelityNavigationStateService } from '../../services/fidelity-navigation-state.service';
import { areObjectsEqual } from '../../utils/fidelity.utils';
import { AlertDialogService, ConfirmDialogService } from 'src/app/dialogs';
import { EntityManagerService, LoaderService } from 'src/app/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TilbyDatetimePickerComponent } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { mobileCheck } from 'src/utilities';

@Component({
  selector: 'app-fidelity-campaigns',
  standalone: true,
  imports: [
    CommonModule,
    MasterDetailLayoutComponent,
    CardComponent,
    SidebarSectionComponent,
    SidebarItemComponent,
    ActionBtnComponent,
    MatIconModule, MatSlideToggleModule, MatButtonModule,
    MatFormFieldModule, ReactiveFormsModule,
    MatInputModule, MatSelectModule,
    TranslateModule, MatDatepickerModule,
    TruncatedTextDirective,
    TilbyDatetimePickerComponent
  ],
  templateUrl: './fidelity-campaigns.component.html',
  styleUrl: './fidelity-campaigns.component.scss'
})
export class FidelityCampaignsComponent implements OnInit, OnDestroy {

  readonly fidelityCampaignFormService = inject(FidelityCampaignFormService);
  private readonly fidelityDataCatalogService = inject(FidelityDataCatalogService);
  private readonly translateService = inject(TranslateService);
  private readonly listSelectorDialogService = inject(ListSelectorDialogService);
  private readonly fidelityNavigationStateService = inject(FidelityNavigationStateService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly loaderService = inject(LoaderService);
  private readonly entityManager = inject(EntityManagerService);
  private readonly _snackBar = inject(MatSnackBar);

  protected campaignsList: ChainCampaigns[] = [];
  protected rulesList: ChainRules[] = [];
  protected itemsList: Items[] = [];
  protected itemsMap: Record<string, Items>;
  protected prizesByCampaign: Record<string | number, CampaignPrizesFields[]>;
  protected selectedCampaign: ChainCampaigns | null = null;

  protected initialData!: CampaignsFields;
  protected campaignForm!: CampaignsFormGroups;
  protected campaignFormReady = this.fidelityCampaignFormService.campaignFormReady;

  protected ruleApplicationTypes = [
    { value: 'dont_apply', name: this.translateService.instant('FIDELITY.SETUP.CAMPAIGNS.DONT_APPLY') },
    { value: 'apply_with_prizes', name: this.translateService.instant('FIDELITY.SETUP.CAMPAIGNS.APPLY_WITH_PRIZES') },
    { value: 'apply_without_prizes', name: this.translateService.instant('FIDELITY.SETUP.CAMPAIGNS.APPLY_WITHOUT_PRIZES') }
  ]

  protected priceLabelClass = "tw-text-[12px] tw-font-bold tw-tracking-wider tw-text-[var(--sys-secondary-text)] tw-mb-1";
  protected priceValueClass = "tw-text-sm tw-font-medium tw-text-[var(--sys-text)]";
  protected isEdit = false;

  protected isMobile = mobileCheck();
  protected showDetailMobile = false;

  constructor() {
    this.campaignsList = this.fidelityDataCatalogService.getDataByKey('campaigns') as ChainCampaigns[];
    this.rulesList = this.fidelityDataCatalogService.getDataByKey('rules') as ChainRules[];
    this.itemsList = this.fidelityDataCatalogService.getDataByKey('items') as Items[];
    this.itemsMap = this.fidelityDataCatalogService.getDataByKey('itemsMap') as Record<string, Items>;
    this.prizesByCampaign = this.fidelityDataCatalogService.getDataByKey('prizesByCampaign') as Record<string | number, CampaignPrizesFields[]>;
  }

  ngOnInit(): void {
    this.fidelityNavigationStateService.register(() => this.checkChanges());
  }

  checkChanges(): boolean {
    if (!this.campaignForm) return false;
    const equal = areObjectsEqual(this.initialData, this.campaignForm.getRawValue());
    return !equal;
  }

  isSelected(campaign: ChainCampaigns) {
    if (this.selectedCampaign) {
      return campaign.id === this.selectedCampaign.id;
    } 
    return false;
  }

  get isNotEmptyState() {
    return this.campaignFormReady() && (this.selectedCampaign || !this.isEdit)
  }

  async selectCampaign($event: any, index: number) {
    $event.stopPropagation();

    const canSelect = await this.checkPendingData();
    if(!canSelect) return;

    this.initCampaign(index);

    if (this.isMobile) {
      this.showDetailMobile = true;
    }
  }

  async goBack() {
    const canSelect = await this.checkPendingData();
    if(!canSelect) return;

    this.campaignForm = this.fidelityCampaignFormService.createForm(this.selectedCampaign);
    this.showDetailMobile = false;
  }

  async checkPendingData() {
    if (this.fidelityNavigationStateService.hasUnsavedChanges()) {
      const confirmed = await this.confirmDialogService.openDialog({
        data: {
          messageLabel: 'FIDELITY.SETUP.CAMPAIGNS.EDIT_CANCEL_CONFIRM',
          confirmLabel: 'APPLICATION.YES',
          cancelLabel: 'APPLICATION.NO'
        }
      });

      if(!confirmed) return false;
    }
    return true;
  }

  async onToggleCampaign(event: any, campaign: ChainCampaigns) {
    const newStatus = event.checked;

    const canSelect = await this.checkPendingData();
    if(!canSelect) {
      event.source.checked = !newStatus;
      return;
    }

    const payload = this.fidelityCampaignFormService.prepareTogglePayload(campaign, newStatus);

    this.loaderService.show();

    try {
      await this.entityManager.campaigns.putOneOnline(payload);
      
      this._snackBar.open(this.translateService.instant('FIDELITY.SETUP.CAMPAIGNS.SAVE_EDIT_OK'), 
        undefined, 
        { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        }
      );

      await this.reloadCampaigns(payload.id);

    } catch (error: any) {
      event.source.checked = !newStatus;
      // the toggle always edits an existing campaign: isEdit belongs to the detail form
      const messageError = 
      error?.data?.error?.code === 71 ? 'FIDELITY.SETUP.CAMPAIGNS.SAVE_CREATE_ERROR' :
        'FIDELITY.SETUP.CAMPAIGNS.SAVE_EDIT_ERROR';
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant(messageError)
      }});
    } finally {
      this.loaderService.hide();
    }
  }

  initCampaign(index: number) {
    if(index < 0) return;

    // out of range (e.g. the last campaign was deleted): nothing is selected and the empty state is shown
    this.selectedCampaign = this.campaignsList[index] ?? null;
    this.campaignForm = this.fidelityCampaignFormService.createForm(this.selectedCampaign);
    this.initialData = structuredClone(this.campaignForm.getRawValue()) as CampaignsFields;
    this.isEdit = true;
  }

  async addCampaign() {
    const canSelect = await this.checkPendingData();
    if(!canSelect) return;

    this.selectedCampaign = null;
    this.campaignForm = this.fidelityCampaignFormService.createForm(this.selectedCampaign);
    this.initialData = structuredClone(this.campaignForm.getRawValue()) as CampaignsFields;
    this.isEdit = false;

    if (this.isMobile) {
      this.showDetailMobile = true;
    }
  }

  addRule() {
    this.fidelityCampaignFormService.addNewRule();
  }

  removeRule(index: number) {
    this.fidelityCampaignFormService.removeRule(index);
  }

  addItem() {
    this.fidelityCampaignFormService.addNewItem();
  }

  removeItem(index: number) {
    this.fidelityCampaignFormService.removeItem(index);
  }

  editItem(index: number) {
    const skuToFind = this.fidelityCampaignFormService.getSkuItem(index)?.value;
    const dataSet = [...this.itemsList];
    const linkedItem = skuToFind ? this.itemsMap[skuToFind] : undefined;

    this.listSelectorDialogService.openDialog({
        data: {
          title: "DIALOG.LIST_SELECTOR.TITLE.ITEM",
          optionList: dataSet, 
          optionValue: linkedItem
        }
      }
    ).then((res: any) => {
      if(res) {
        this.fidelityCampaignFormService.getNameItem(index)?.setValue(res.name);
        this.fidelityCampaignFormService.getSkuItem(index)?.setValue(res.sku);
      }
    })
  }

  async deleteCampaign() {
    const idCampaign = this.selectedCampaign?.id;
    if(!idCampaign) {
      const canSelect = await this.checkPendingData();
      if(!canSelect) return;

      this.initCampaign(0);
      return;
    }

    const confirmed = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'FIDELITY.SETUP.CAMPAIGNS.DELETE_CONFIRM',
        confirmLabel: 'APPLICATION.YES',
        cancelLabel: 'APPLICATION.NO'
      }
    });

    if(!confirmed) return;

    this.loaderService.show();

    try {
      await this.entityManager.campaigns.deleteOneOnline(idCampaign);

      this._snackBar.open(this.translateService.instant('FIDELITY.SETUP.CAMPAIGNS.DELETE_OK'),
        undefined, 
        { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        }
      );

      await this.reloadCampaigns();
      
      if (this.isMobile) {
        this.showDetailMobile = false;
      }
    } catch(error) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.CAMPAIGNS.DELETE_ERROR')
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  async saveCampaign() {
    if (this.campaignForm.invalid) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.CAMPAIGNS.SAVE_INCORRECT_DATA')
      }});
      return;
    }

    const campaignToSend = this.fidelityCampaignFormService.prepareDataForSave();

    if (campaignToSend.items?.length === 0 && this.fidelityCampaignFormService.itemsArray?.length) {
      // Se l'utente aveva inserito righe ma sono tutte incomplete
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.CAMPAIGNS.MISSING_ITEM_INFO')
      }});
      return;
    }

    this.loaderService.show();
    const isEdit = !!campaignToSend.id;

    try {
      const result: ChainCampaigns = isEdit 
        ? await this.entityManager.campaigns.putOneOnline(campaignToSend)
        : await this.entityManager.campaigns.postOneOnline(campaignToSend);

        this._snackBar.open(this.translateService.instant(isEdit ? 'FIDELITY.SETUP.CAMPAIGNS.SAVE_EDIT_OK' : 'FIDELITY.SETUP.CAMPAIGNS.SAVE_CREATE_OK'),
          undefined, 
          { 
            duration: 3000, 
            horizontalPosition: 'start', 
            verticalPosition: "bottom", 
          }
        );

        await this.reloadCampaigns(result.id);

        if (this.isMobile) {
          this.showDetailMobile = false;
        }
    } catch (error: any) {
      const messageError = 
      error?.data?.error?.code === 71 ? 'FIDELITY.SETUP.CAMPAIGNS.SAVE_CREATE_ERROR' :
        isEdit ? 'FIDELITY.SETUP.CAMPAIGNS.SAVE_EDIT_ERROR' : 
        'FIDELITY.SETUP.CAMPAIGNS.SAVE_CREATE_GENERIC_ERROR';
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant(messageError)
      }});
    } finally {
      this.loaderService.hide();
    }
  }

  async updateCampaigns(id?: number) {
    const updatedList = await this.entityManager.campaigns.fetchCollectionOnline({});
    this.campaignsList = updatedList as ChainCampaigns[];

    this.fidelityDataCatalogService.addDataToCatalog('campaigns', [...this.campaignsList]);

    const newIndex = id? this.campaignsList.findIndex(c => c.id === id) : 0;
    this.initCampaign(Math.max(newIndex, 0));
  }

  /** Reloads the list after a change: a failure must not be reported as a failure of the change itself, which succeeded */
  private async reloadCampaigns(id?: number) {
    try {
      await this.updateCampaigns(id);
    } catch (error) {
      console.error('Failed to reload the campaigns', error);
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.LOAD_ERROR')
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.fidelityNavigationStateService.unregister();
    this.campaignFormReady.set(false);
  }
}
